import React from 'react';
import { motion } from 'framer-motion';
import { 
  Search, ArrowRight, Sparkles, Crown, Zap, Eye, Building, Waves, 
  Target, Star, Clock, Brain, Trophy, Gamepad2, Shield, Lock,
  PlayCircle, TrendingUp, Heart,
} from 'lucide-react';

const VibeLifeDesignHero = ({ onExplore }) => {
  const lifeDesignTools = [
    {
      id: 'opportunity-detective',
      icon: Eye,
      title: 'Opportunity Detective',
      subtitle: 'What are you really trading your time for?',
      description: 'Discover the hidden costs of your choices. Spoiler: it\'s not just time you\'re spending.',
      color: 'from-orange-500 to-red-500',
      badge: 'Detective Mode',
      emoji: '🔍',
      coolFactor: 'CSI but for your life choices',
    },
    {
      id: 'choice-architect',
      icon: Building,
      title: 'Choice Architect',
      subtitle: 'Design your environment for success',
      description: 'Stop relying on willpower. Design spaces that make your future self proud.',
      color: 'from-emerald-500 to-cyan-500',
      badge: 'Environment Hacker',
      emoji: '🏗️',
      coolFactor: 'Interior design meets life optimisation',
    },
    {
      id: 'legacy-builder',
      icon: Waves,
      title: 'Legacy Builder',
      subtitle: 'Create ripple effects that matter',
      description: 'Transform from someone who improves themselves to someone who improves the world.',
      color: 'from-purple-500 to-pink-500',
      badge: 'Impact Creator',
      emoji: '🌊',
      coolFactor: 'Your growth becomes everyone\'s inspiration',
    },
  ];

  return (
<section className="py-20 bg-gradient-to-br from-emerald-900/30 to-slate-900/50 relative overflow-hidden">

      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            {/* Floating Elements */}
            <div className="relative">
              <motion.div
                animate={{ y: [-5, 5, -5], rotate: [0, 5, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute -top-8 -left-8 text-6xl"
              >
                🕵️‍♀️
              </motion.div>
              <motion.div
                animate={{ y: [5, -5, 5], rotate: [0, -5, 5, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                className="absolute -top-4 -right-8 text 6xl"
              >
                🏗️
              </motion.div>
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity, delay: 2 }}
                className="absolute top-12 left-12 text-4xl"
              >
                🌊
              </motion.div>
            </div>

            <div className="flex items-center justify-center gap-2 mb-4">
              <Crown className="w-6 h-6 text-yellow-400" />
              <span className="px-3 py-1 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-full text-emerald-300 font-medium">
                🔥 Bonus ADDON - Advanced Level Unlocked
              </span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Life Design Series
            </h2>
            
            <div className="max-w-4xl mx-auto space-y-4">
              <h3 className="text-2xl md:text-3xl text-emerald-300 font-bold">
                Ever Wonder What You're Actually Trading Your Time For? 🤔
              </h3>
              <p className="text-xl text-slate-300 leading-relaxed">
                Stop living on autopilot. These advanced tools turn you into a detective of your own choices, 
                an architect of your environment, and someone who creates ripple effects that matter.
              </p>
              <p className="text-emerald-300 font-medium flex items-center justify-center gap-2">
                <Zap className="w-4 h-4 text-yellow-400" />
                <span className="text-white">Real talk:</span> You're already designing your life with every choice. 
                Why not do it consciously?
              </p>
            </div>
          </motion.div>

          {/* Tools Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {lifeDesignTools.map((tool, index) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="h-full"
              >
                <div className={`bg-gradient-to-br ${tool.color}/10 backdrop-blur-lg border border-${tool.color.split('-')[1]}-500/30 rounded-xl p-6 relative overflow-hidden group hover:border-${tool.color.split('-')[1]}-400/50 transition-all duration-300 h-full`}>
                  
                  {/* Background glow effect */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${tool.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                  
                  <div className="relative space-y-4 h-full flex flex-col">
                    {/* Header */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${tool.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform relative`}>
                          <tool.icon className="w-8 h-8 text-white" />
                          <div className="absolute -top-2 -right-2 text-lg">
                            {tool.emoji}
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                          tool.badge === 'Detective Mode' ? 'bg-orange-500/20 text-orange-300 border-orange-500/30' :
                          tool.badge === 'Environment Hacker' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
                          'bg-purple-500/20 text-purple-300 border-purple-500/30'
                        }`}>
                          {tool.badge}
                        </span>
                      </div>
                      
                      <div>
                        <h3 className="text-xl font-bold text-white group-hover:text-emerald-200 transition-colors">
                          {tool.title}
                        </h3>
                        <p className="text-emerald-200 font-medium">
                          {tool.subtitle}
                        </p>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 space-y-4">
                      <p className="text-slate-300 leading-relaxed">
                        {tool.description}
                      </p>
                      
                      {/* Cool Factor */}
                      <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                        <p className="text-yellow-300 text-sm font-medium">
                          ✨ {tool.coolFactor}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Why This Matters - Enhanced */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="bg-gradient-to-r from-emerald-500/10 to-purple-500/10 backdrop-blur-sm rounded-xl p-8 border border-emerald-500/30 mb-12"
          >
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center justify-center gap-2">
              <Zap className="w-6 h-6 text-yellow-400" />
              Why Your Future Self Will Thank You
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-emerald-400 rounded-full mt-1.5"></div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Stop Making Choices on Autopilot</h4>
                    <p className="text-slate-300 text-sm">Make decisions consciously instead of just reacting to whatever's in front of you.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-blue-400 rounded-full mt-1.5"></div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Design Environments That Serve You</h4>
                    <p className="text-slate-300 text-sm">Create spaces that make good choices easier than bad ones.</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-purple-400 rounded-full mt-1.5"></div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Create Positive Ripple Effects</h4>
                    <p className="text-slate-300 text-sm">See how your growth can lift others up and create lasting impact.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-yellow-400 rounded-full mt-1.5"></div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Become Who You Want to Be</h4>
                    <p className="text-slate-300 text-sm">Connect with your future self and make choices that serve your dreams.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center mb-12"
          >
            {[
              { stat: '4', label: 'New Modules', icon: Brain, color: 'text-emerald-400' },
              { stat: '3', label: 'Interactive Tools', icon: Gamepad2, color: 'text-blue-400' },
              { stat: '∞', label: 'Life Impact', icon: Trophy, color: 'text-yellow-400' },
              { stat: '1', label: 'You', icon: Heart, color: 'text-pink-400' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 1 + index * 0.1 }}
                className="group"
              >
                <div className="w-16 h-16 bg-white/5 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-white/10 transition-colors">
                  <item.icon className={`w-6 h-6 ${item.color}`} />
                </div>
                <div className="text-2xl font-bold text-white mb-1 group-hover:text-emerald-300 transition-colors">
                  {item.stat}
                </div>
                <div className="text-slate-400 text-sm">
                  {item.label}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 1.2 }}
            className="text-center"
          >
            <div className="bg-gradient-to-br from-emerald-900/60 to-purple-900/60 rounded-2xl p-8 border border-emerald-500/30 max-w-3xl mx-auto">
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center justify-center gap-2">
                <Search className="w-6 h-6 text-emerald-400" />
                Ready to Start Your Detective Work?
              </h3>
              <p className="text-emerald-200 mb-6">
                These aren't just modules - they're like having a personal life coach who actually gets our generation. 
                No more living on autopilot, time to design the life you actually want.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <motion.button
                  onClick={onExplore}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2"
                >
                  <PlayCircle className="w-5 h-5" />
                  Explore Life Design Series
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </div>
              
              <div className="flex items-center justify-center gap-6 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-emerald-400" />
                  <span>Best after foundation work</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-400" />
                  <span>Advanced level content</span>
                </div>
              </div>
              
              {/* Bottom hook */}
              <div className="mt-6 pt-4 border-t border-emerald-500/20">
                <div className="flex items-center justify-center gap-2 text-center">
                  <Sparkles className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm text-emerald-200">
                    Go from <span className="text-white font-medium">reactive</span> to <span className="text-white font-medium">intentional</span> - 
                    and watch your life actually start making sense
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default VibeLifeDesignHero;